import { FC } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import { OutlineCtaLink } from '@/components/common/OutlineCtaLink';
import { RevealOnScroll } from '@/components/common/RevealOnScroll';

export type HighlightRecognitionProps =
  SliceComponentProps<Content.HighlightRecognitionSlice>;

const HighlightRecognition: FC<HighlightRecognitionProps> = ({ slice }) => {
  const { image, eyebrow, description, cta } = slice.primary;

  const hasContent =
    isFilled.image(image) ||
    !!eyebrow ||
    isFilled.richText(description) ||
    isFilled.link(cta);

  if (!hasContent) {
    return null;
  }

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="px-4 py-[60px] md:px-[30px] md:py-[80px]"
    >
      <RevealOnScroll className="relative z-[1] mx-auto w-full max-w-[1280px]">
        <div className="flex w-full flex-col overflow-hidden rounded-[15px] border border-[#aaa] lg:flex-row lg:items-stretch">
          {isFilled.image(image) && (
            <div className="relative h-[190px] w-full shrink-0 overflow-hidden rounded-t-[15px] [@media(min-width:375px)]:h-[240px] sm:h-[280px] md:h-[340px] lg:h-auto lg:min-h-[256px] lg:w-[379px] lg:rounded-t-none lg:rounded-l-[15px]">
              <PrismicNextImage
                field={image}
                fill
                sizes="(min-width: 1024px) 379px, 100vw"
                className="scale-105 object-cover md:scale-110"
                fallbackAlt=""
              />
            </div>
          )}

          <div className="flex flex-1 flex-col items-start justify-center gap-4 p-6 lg:py-6 lg:pl-9 lg:pr-8">
            {eyebrow && (
              <p className="w-full text-[14px] font-normal uppercase leading-[1.2] text-[#737373]">
                {eyebrow}
              </p>
            )}

            {isFilled.richText(description) && (
              <div className="w-full max-w-[841px] break-words font-inter text-[18px] font-medium leading-[1.4] text-[#041118] md:text-[20px] lg:text-[24px] [&_p]:m-0 [&_p+p]:mt-2">
                <PrismicRichText
                  field={description}
                  components={{
                    paragraph: ({ children }) => <p>{children}</p>,
                    strong: ({ children }) => (
                      <strong className="font-semibold">{children}</strong>
                    ),
                  }}
                />
              </div>
            )}

            {isFilled.link(cta) && (
              <div className="w-full pt-[8px] [@media(min-width:375px)]:w-auto">
                <OutlineCtaLink
                  field={cta}
                  stripTrailingArrow
                  className="w-full whitespace-normal [@media(min-width:375px)]:w-auto [@media(min-width:375px)]:whitespace-nowrap"
                />
              </div>
            )}
          </div>
        </div>
      </RevealOnScroll>
    </section>
  );
};

export default HighlightRecognition;
